import IconMinusCircle from "@shared/components/icons/MinusCircle"
import IconTrashBin from "@shared/components/icons/TrashBin"
import { ChoiceCards } from "@shared/components/menus/views/ChoiceCards"
import { ViewShell } from "@shared/components/menus/views/ViewShell"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { useState } from "react"

/** How far a unit deletion reaches: the unit alone, or its lessons with it. */
export type UnitDeletionScope = "unit-only" | "unit-and-lessons"

/**
 * "Delete this unit?" — the drill-down that replaces the Ember `dialogs/confirm-delete-unit`
 * dialog, whose two buttons ("Delete the Unit" / "Delete the Unit AND lessons") are the two
 * cards here.
 */
export function DeleteUnitView({
  onCancel,
  onDelete,
}: {
  onCancel: () => void
  /** Called with the chosen scope; the caller erases the unit and closes the menu. */
  onDelete: (scope: UnitDeletionScope) => void
}) {
  const [scope, setScope] = useState<UnitDeletionScope | null>(null)

  return (
    <ViewShell
      actions={
        <>
          <Button color="secondary" size="md" className="flex-1" onPress={onCancel}>
            Cancel
          </Button>
          <Button
            data-test="view confirm"
            color="primary-destructive"
            size="md"
            className="flex-1"
            isDisabled={scope === null}
            onPress={() => scope !== null && onDelete(scope)}
          >
            Delete
          </Button>
        </>
      }
    >
      {/* Deletion Scope Chooser */}
      <div className="px-5 pt-4">
        <ChoiceCards<UnitDeletionScope>
          ariaLabel="How much of this unit should be deleted?"
          value={scope}
          onChange={setScope}
          options={[
            {
              value: "unit-only",
              label: "Just the unit",
              description: "The lessons in it stay where they are",
              icon: IconMinusCircle,
            },
            {
              value: "unit-and-lessons",
              label: "The unit and its lessons",
              description: "Erases every lesson the unit covers",
              icon: IconTrashBin,
            },
          ]}
        />
      </div>
    </ViewShell>
  )
}
